import React, { useEffect, useState } from 'react';
import AccountWishlist from '../components/account/AccountWishlist';
import AccountOrders from '../components/account/AccountOrders';
import WalletPage from './WalletPage';
import UserAdress from '../components/account/UserAddress';
import CouponsSection from '../components/account/CouponsSection';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from "react-router-dom";
import { getMyProfile, updateMyProfile } from "../services/userService";
import '../css/userDashboard.css';
import '../css/account.css';
const UserDashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    mobile: '',
    gender: '',
    dateOfBirth: '',
    whatsappOptIn: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 🔹 which tab is open on the right side: 'profile' or 'address'
  const [activeTab, setActiveTab] = useState('profile');


  const [saved, setSaved] = useState('');
  // Open a specific tab when sent here with { state: { activeTab } }
  const { state } = useLocation();

  useEffect(() => {
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
    }
  }, [state]);

  // ---------- LOAD PROFILE FROM BACKEND ----------
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getMyProfile();
        setProfile({
          name: data.name || user?.name || '',
          // phone-login accounts get a placeholder address — show it blank
          email: /\.local$/i.test(data.email || '') ? '' : (data.email || user?.email || ''),
          mobile:
            data.mobile ||
            data.phone ||
            user?.mobile ||
            user?.phone ||
            '',
          gender: data.gender || '',
          dateOfBirth: data.dateOfBirth
            ? data.dateOfBirth.substring(0, 10)
            : '',
          whatsappOptIn: !!data.whatsappOptIn
        });
      } catch (err) {
        console.error('Failed to load profile', err);
        if (err.response?.status === 401) {
          navigate('/auth');
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate, user]);

  const initialLetter =
    profile.name?.[0]?.toUpperCase() ||
    user?.name?.[0]?.toUpperCase() ||
    'U';

  const handleChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (/^User-\d{4}$/.test(profile.name.trim()) || profile.name.trim().length < 2) {
      setSaved('error:Please enter your full name');
      return;
    }
    setSaving(true);
    setSaved('');
    try {
      const updated = await updateMyProfile({ ...profile, name: profile.name.trim() });
      const name = updated.name || profile.name.trim();
      const email = /\.local$/i.test(updated.email || '') ? '' : updated.email || profile.email;
      setProfile((prev) => ({ ...prev, name, email }));
      // header shows the new name straight away
      updateUser({ name, email: updated.email || user?.email });
      setSaved('ok:Your profile has been saved');
    } catch (err) {
      console.error('Update failed', err);
      setSaved(`error:${err.response?.data?.message || 'Could not save your profile. Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const TABS = [
    { key: 'profile', label: 'My profile', note: 'Your name and contact details', icon: 'user' },
    { key: 'orders', label: 'My orders', note: 'Track, return or reorder', icon: 'bag' },
    { key: 'address', label: 'Addresses', note: 'Where we deliver your orders', icon: 'pin' },
    { key: 'wishlist', label: 'Wishlist', note: 'Pieces you saved for later', icon: 'heart' },
    { key: 'wallet', label: 'Mithralaya wallet', note: 'Balance and transactions', icon: 'wallet' },
    { key: 'coupons', label: 'Coupons', note: 'Offers you can use', icon: 'ticket' }
  ];
  const current = TABS.find((t) => t.key === activeTab) || TABS[0];
  const placeholderName = /^User-\d{4}$/.test(profile.name || '');
  const phoneDisplay = profile.mobile
    ? `+91 ${String(profile.mobile).replace(/\D/g, '').slice(-10).replace(/(\d{5})(\d{5})/, '$1 $2')}`
    : '';

  if (loading) {
    return <div className="acc-loading" role="status">Loading your account…</div>;
  }

  return (
    <div className="acc-page">
      {/* ---------- Sidebar ---------- */}
      <aside className="acc-side">
        <div className="acc-user">
          <div className="acc-avatar" aria-hidden="true">{placeholderName ? '★' : initialLetter}</div>
          <div className="acc-user-text">
            <span className="acc-hello">Hello,</span>
            <strong>{placeholderName ? 'there' : profile.name}</strong>
            {phoneDisplay && <span>{phoneDisplay}</span>}
          </div>
        </div>

        <nav className="acc-nav" aria-label="Account">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`acc-nav-item ${activeTab === t.key ? 'is-active' : ''}`}
              aria-current={activeTab === t.key ? 'page' : undefined}
              onClick={() => { setActiveTab(t.key); setSaved(''); }}
            >
              <AccIcon name={t.icon} />
              <span>{t.label}</span>
            </button>
          ))}
          <button type="button" className="acc-nav-item" onClick={() => navigate('/contact')}>
            <AccIcon name="mail" />
            <span>Help &amp; contact</span>
          </button>
          <button type="button" className="acc-nav-item acc-logout" onClick={handleLogout}>
            <AccIcon name="logout" />
            <span>Log out</span>
          </button>
        </nav>
      </aside>

      {/* ---------- Content ---------- */}
      <main className="acc-main">
        <header className="acc-main-head">
          <h1>{current.label}</h1>
          <p>{current.note}</p>
        </header>

        <div className="acc-main-body">
          {activeTab === 'address' ? (
            <UserAdress />
          ) : activeTab === 'wishlist' ? (
            <AccountWishlist />
          ) : activeTab === 'orders' ? (
            <AccountOrders />
          ) : activeTab === 'wallet' ? (
            <WalletPage />
          ) : activeTab === 'coupons' ? (
            <CouponsSection />
          ) : (
            <form className="acc-form" onSubmit={handleSave} noValidate>
              {placeholderName && (
                <p className="acc-callout">Add your name so we can address your orders and messages properly.</p>
              )}

              <div className="acc-grid">
                <label className="acc-field">
                  <span>Full name <em>*</em></span>
                  <input
                    id="fullNameField"
                    type="text"
                    autoComplete="name"
                    value={placeholderName ? '' : profile.name}
                    placeholder="e.g. Priya Raman"
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                  />
                </label>

                <label className="acc-field">
                  <span>Email <small>(for order emails)</small></span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={profile.email}
                    placeholder="name@example.com"
                    onChange={(e) => handleChange('email', e.target.value.trim())}
                  />
                </label>

                <div className="acc-field">
                  <span>Mobile number</span>
                  <div className="acc-readonly">
                    {phoneDisplay || '—'}
                    <small>Used to log in</small>
                  </div>
                </div>

                <label className="acc-field">
                  <span>Date of birth <small>(optional)</small></span>
                  <input
                    type="date"
                    value={profile.dateOfBirth}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  />
                </label>
              </div>

              <fieldset className="acc-field acc-gender">
                <legend>Gender <small>(optional)</small></legend>
                <div className="acc-segment" role="radiogroup">
                  {[['female', 'Female'], ['male', 'Male'], ['other', 'Other']].map(([v, label]) => (
                    <label key={v} className={profile.gender === v ? 'is-on' : ''}>
                      <input
                        type="radio"
                        name="gender"
                        value={v}
                        checked={profile.gender === v}
                        onChange={() => handleChange('gender', v)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="acc-check">
                <input
                  type="checkbox"
                  checked={profile.whatsappOptIn}
                  onChange={(e) => handleChange('whatsappOptIn', e.target.checked)}
                />
                <span>Send me order updates on WhatsApp</span>
              </label>

              {saved && (
                <p className={`acc-msg ${saved.startsWith('ok:') ? 'is-ok' : 'is-error'}`} role="status">
                  {saved.slice(saved.indexOf(':') + 1)}
                </p>
              )}

              <div className="acc-actions">
                <button type="submit" className="acc-save" disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

// Simple line icons (replace the old emoji icons)
const ICONS = {
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21c1-4 4.5-6 8-6s7 2 8 6" /></>,
  bag: <><path d="M5 8h14l-1 12H6L5 8z" /><path d="M9 8V6a3 3 0 016 0v2" /></>,
  pin: <><path d="M12 21s-7-6-7-11a7 7 0 0114 0c0 5-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>,
  heart: <path d="M12 20.5s-7.5-4.6-9.2-9.4C1.6 7.6 3.9 4 7.4 4c2 0 3.5 1.1 4.6 2.7C13.1 5.1 14.6 4 16.6 4c3.5 0 5.8 3.6 4.6 7.1-1.7 4.8-9.2 9.4-9.2 9.4z" />,
  wallet: <><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18M16 14.5h2" /></>,
  ticket: <><path d="M4 8a2 2 0 002-2h12a2 2 0 002 2v2a2 2 0 000 4v2a2 2 0 00-2 2H6a2 2 0 00-2-2v-2a2 2 0 000-4V8z" /><path d="M10 6v12" strokeDasharray="2 2" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
  logout: <><path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3" /><path d="M10 16l-4-4 4-4M6 12h10" /></>
};
const AccIcon = ({ name }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

export default UserDashboard;
